﻿import { toast } from 'sonner';
import i18next from 'i18next';

import { useWorkflowTaskStore } from '@/store/workflow-task.store.js';

async function refreshMyWorkPendingSilent() {
    const { myWorkIntegration } = await import('../api/integrations-registry.js');
    await myWorkIntegration.fetchMyPending({}, { silent: true });
}

export class WorkflowTaskIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * @param {Record<string, unknown>} [params]
     * @param {{ silent?: boolean }} [options] â€” silent: no global loading/toast (e.g. badge prefetch)
     */
    async fetchMyInbox(params = {}, options = {}) {
        const { silent = false } = options;
        const store = useWorkflowTaskStore.getState();
        if (!silent) {
            store.setLoading(true);
            store.clearError();
        }
        try {
            const response = await this.service.myInbox(params);
            store.setInbox(response.data || []);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            store.setError(msg);
            if (!silent) toast.error(msg);
            return { success: false, error: msg };
        } finally {
            if (!silent) store.setLoading(false);
        }
    }

    async fetchTasks(params = {}) {
        const store = useWorkflowTaskStore.getState();
        store.setLoading(true);
        store.clearError();
        try {
            const response = await this.service.getAll(params);
            store.setItems(response.data || []);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = this._err(error);
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * @param {string} id
     * @param {string | Record<string, unknown>} decisionOrBody â€” string decision or full POST body
     */
    async decide(id, decisionOrBody) {
        try {
            const body = typeof decisionOrBody === 'string'
                ? { decision: decisionOrBody }
                : { ...decisionOrBody };
            const response = await this.service.decide(id, body);
            const msg = response.data?.formTask
                ? i18next.t('workflow:messages.infoRequestedSuccess', {
                    defaultValue: 'Form task created for information request',
                })
                : i18next.t('workflow:messages.decisionSuccess', { defaultValue: 'Decision saved' });
            toast.success(msg);
            await this.fetchMyInbox({}, { silent: true });
            await refreshMyWorkPendingSilent();
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._err(error));
            return { success: false, error: error.message };
        }
    }

    /**
     * FORM_FILL: submit answers; API creates FormSubmission and completes the task.
     */
    async submitFormFill(taskId, body) {
        try {
            const response = await this.service.submitFormFill(taskId, body);
            toast.success(
                i18next.t('workflow:messages.formFillSuccess', { defaultValue: 'Form submitted' }),
            );
            await this.fetchMyInbox({}, { silent: true });
            await refreshMyWorkPendingSilent();
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._err(error));
            return { success: false, error: error.message };
        }
    }

    async forward(taskId, body) {
        try {
            const response = await this.service.forward(taskId, body || {});
            toast.success(
                i18next.t('workflow:messages.forwardSuccess', { defaultValue: 'Task forwarded' }),
            );
            await this.fetchMyInbox({}, { silent: true });
            await refreshMyWorkPendingSilent();
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._err(error));
            return { success: false, error: error.message };
        }
    }

    _err(error) {
        if (error?.isValidationError?.()) return i18next.t('common:messages.validationError');
        if (error?.isNotFound?.()) return i18next.t('common:messages.notFoundError');
        return error.message || i18next.t('common:messages.unexpectedError');
    }
}
